/**
 * Copyright (c) Microsoft. All rights reserved.
 * Internal type definitions for Trouter JavaScript Client
 */

import { calculateTtlInSec } from './Common';
import {
    HttpHeaders, IConnectionCache, IConnectionInfo, ITelemetrySender, SendResponseResult,
    TimeoutOptions, TrouterTelemetrySettings
} from './Interfaces';

// tslint:disable:no-any
// tslint:disable:interface-name

export interface ITrouterRequest {
    startTS: number;
    url: string;
    shortUrl: string;
    body: string;
    headers: HttpHeaders;
    id: number;
    method: string;
    correlationVector: string;
}

export interface IMessageLossIndicator {
    /** flow tag representing the type of lost messages */
    tag: string;

    /**
     * etag of the particular flow tag, the etag is used to identify the
     * flow tag on the server when sending trouter.processed_message_loss event
     * to be able to determine if new message loss happened after this one or not
     */
    etag: string;
}

/**
 * Response to downstream service
 */
export interface ITrouterResponse {
    writeHead(status: number, headers?: HttpHeaders);
    write(chunk: string);
    end(chunk?: string): SendResponseResult;
}

/**
 * Data to generate TC query param and for telemetry
 */
export interface IClientInfo {
    /** User-Agent string, for telemetry tracking */
    ua: string;
    /** Application client version, for telemetry tracking */
    v: string;
    /** Trouter client version */
    cv?: string;
}

/**
 * Socket.IO (aka "transport") options
 */
export interface ISocketIoOptions {
    'force new connection'?: boolean;
    'reconnect'?: boolean;
    'query'?: string;
    /** timeout for each transport */
    'connect timeout'?: number;
    transports?: string[];
    /**
     * Timeout for waiting for ACK from server for trouter response (i.e. POST HTTP request) in XHR mode
     */
    ackTimeoutMs: number;
}

/**
 * EDF registrar options
 */
export interface IRegistrarOptions {
    registrarUrl: string;
    registrationId: string;

    pnhAppId: string;
    platform: string;
    pnhTemplateKey: string;
    platformUIVersion: string;
    productContext?: string;
    context: string;

    /** If set - limits registration TTL in EDF. If not - value from Trouter is used */
    registrarTtlSec?: number;
}

/**
 * Options passed from outside
 */
export interface IConfiguration {
    clientInfo: IClientInfo;

    eventLogger: ITelemetrySender;
    telemetrySettings: TrouterTelemetrySettings;  // Client telemetry configuration settings

    clientCorrelationID: string;  // Client telemetry correlation ID, for correlation between Trouter and other components
    environment: string;  // Environment for telemetry, e.g. "latest", "liveint", "dogfood", "prod"

    ioOptions?: ISocketIoOptions;  // Socket.IO options
    io?: any;  // Socket.IO override; for tests only
}

/**
 * Options passed from outside
 */
export interface ITrouterOptions extends IConfiguration {
    endpointId?: string;
    trouterUrl: string;
    registration?: IRegistrarOptions;

    /**
     * timeouts for normal mode
     */
    timeoutOptions: TimeoutOptions;

    /**
     * timeouts for in-call mode, i.e. with more aggressive timeouts/reconnect policy
     */
    incallTimeoutOptions: TimeoutOptions;

    /**
     * Timeout for being in 'in-call' mode, i.e. with aggressive ping/retry policy. If it's zero - in-call mode is disabled
     */
    incallModeTimeoutMs: number;

    /**
     * Delay for keeping a previous connection open after connection switching.
     */
    lingeringConnectionDelayMs: number;

    /**
     * After how long to send the user activity second time after reconnect.
     */
    userActivitySecondResendDelayMs: number;

    /**
     * How soon after a connection is established must a disconnect packet with
     * the "dup" reason come to be recognized as a "duplicate loop/storm" with
     * another client sharing the same connection cache and trigger recovery.
     */
    duplicateDisconnectThresholdMs: number;

    connectionCache?: IConnectionCache;

    /**
     * Optional setting to put a limit on maximum number of token fetch retries
     */
    retryLimitOnTokenFetch?: number;

    registrationStateCallback?(isRegistered: boolean): void;
}

/**
 * Information about server connection
 */
export class ServerState implements IConnectionInfo {
    public constructor(
        /** Trouter client connection ID */
        public connectionId: string,
        /** Trouter client connected client ID */
        public connectedClientId: string,
        /** window.location.hostname */
        public domId: string,
        /** Unsecure Trouter url at which client can be reached */
        public unsecureUrl: string,
        /** Secure Trouter url at which client can be reached */
        public url: string,
        /** URL base to use for client-to-client requests */
        public c2cUrlBase: string,
        /** expiration timestamp for connection in seconds */
        public expirationTsSec?: number
    ) {
    }

    /** Return connection TTL, i.e. number of seconds till expiration */
    public getRemainingTtlInSec(): number {
        return calculateTtlInSec(this.expirationTsSec);
    }
}

/**
 * Interface for trouter URL promise resolver
 */
export interface ITrouterUrlPromise {
    resolveUrl(url: string): void;
    resetUrl(): void;
}

export enum UserActivityEventReason {
    Unknown = 0,
    Modified = 1,
    Snapshot = 2,
    Connected = 3
}



// WEBPACK FOOTER //
// ./lib_temp/InternalInterfaces.ts